import { createSlice } from '@reduxjs/toolkit';

const initialResults = [
  {
    name: 'Vitamine A',
    value: '2.56',
    unit: 'µmol/L',
    status: 'optimal',
    ranges: {
      min: 0,
      low: 1.05,
      high: 2.09,
      optimalMax: 2.8,
      max: 2.8,
      current: 2.56,
    },
  },
  {
    name: 'Vitamine B6',
    value: '62',
    unit: 'µmol/L',
    status: 'optimal',
    ranges: {
      min: 0,
      low: 20,
      high: 41.2,
      optimalMax: 247,
      max: 247,
      current: 62,
    },
  },
  {
    name: 'Foliumzuur (B11)',
    value: '31.1',
    unit: 'nmol/L',
    status: 'optimal',
    ranges: {
      min: 0,
      low: 8.9,
      high: 22.6,
      optimalMax: 56.5,
      max: 56.5,
      current: 31.1,
    },
  },
  {
    name: 'Vitamine B12',
    value: '600',
    unit: 'pmol/L',
    status: 'optimal',
    ranges: {
      min: 0,
      low: 145,
      high: 442,
      optimalMax: 664,
      max: 664,
      current: 600,
    },
  },
  {
    name: 'Vitamine D3',
    value: '123',
    unit: 'nmol/L',
    status: 'optimal',
    ranges: {
      min: 0,
      low: 75,
      high: 100,
      optimalMax: 150,
      max: 150,
      current: 123,
    },
  },
  {
    name: 'Vitamine E',
    value: '31.1',
    unit: 'µmol/L',
    status: 'optimal',
    ranges: {
      min: 0,
      low: 12.8,
      high: 25.0,
      optimalMax: 35.0,
      max: 41.8,
      current: 31.1,
    },
  },
  {
    name: 'IJzer',
    value: '34',
    unit: 'µmol/L',
    status: 'optimal',
    ranges: {
      min: 0,
      low: 5.9,
      high: 10.7,
      optimalMax: 34.5,
      max: 34.5,
      current: 34,
    },
  },
  {
    name: 'Natrium',
    value: '142',
    unit: 'nmol/L',
    status: 'optimal',
    ranges: {
      min: 0,
      low: 136,
      high: 138,
      optimalMax: 144,
      max: 145,
      current: 142,
    },
  },
  {
    name: 'Magnesium',
    value: '0.94',
    unit: 'mmol/L',
    status: 'optimal',
    ranges: {
      min: 0,
      low: 0.66,
      high: 0.9,
      optimalMax: 1.1,
      max: 1.1,
      current: 0.94,
    },
  },
  {
    name: 'TSH',
    value: '5',
    unit: 'mU/L',
    status: 'attention',
    ranges: {
      min: 0,
      low: 0.27,
      high: 0.5,
      optimalMax: 2.0,
      max: 4.2,
      current: 5.0,
    },
  },
  {
    name: 'CRP',
    value: '7',
    unit: 'mg/L',
    status: 'attention',
    ranges: {
      min: 0,
      low: 1.0,
      high: 1.0,
      optimalMax: 5,
      max: 5,
      current: 7.0,
    },
  },
  {
    name: 'Calcium',
    value: '2.38',
    unit: 'mmol/L',
    status: 'acceptable',
    ranges: {
      min: 0,
      low: 2.15,
      high: 2.25,
      optimalMax: 2.4,
      max: 2.5,
      current: 2.38,
    },
  },
  {
    name: 'Cholesterol',
    value: '5.08',
    unit: 'mmol/L',
    status: 'acceptable',
    ranges: {
      min: 0,
      low: 3.9,
      high: 5.0,
      optimalMax: 5.18,
      max: 5.18,
      current: 5.08,
    },
  },
  {
    name: 'HDL',
    value: '1.14',
    unit: 'mmol/L',
    status: 'attention',
    ranges: {
      min: 0,
      low: 1.17,
      high: 1.49,
      optimalMax: 1.5,
      max: 1.5,
      current: 1.14,
    },
  },
  {
    name: 'LDL',
    value: '3.21',
    unit: 'mmol/L',
    status: 'acceptable',
    ranges: {
      min: 0,
      low: 0,
      high: 2.5,
      optimalMax: 3.37,
      max: 3.37,
      current: 3.21,
    },
  },
  {
    name: 'Triglyceriden',
    value: '1.49',
    unit: 'mmol/L',
    status: 'optimal',
    ranges: {
      min: 0,
      low: 0,
      high: 1.5,
      optimalMax: 2.28,
      max: 2.28,
      current: 1.49,
    },
  },
  {
    name: 'ALAT',
    value: '34',
    unit: 'U/L',
    status: 'acceptable',
    ranges: { min: 0, low: 0, high: 30, optimalMax: 35, max: 35, current: 34 },
  },
  {
    name: 'Alkalische fosfatase',
    value: '171',
    unit: 'U/L',
    status: 'attention',
    ranges: {
      min: 0,
      low: 40,
      high: 50,
      optimalMax: 100,
      max: 130,
      current: 171,
    },
  },
];

const initialRecommendations = [
  {
    title: 'Vitamine A, B6 en E',
    content:
      'Deze waarden zijn niet bepaald door onvoldoende materiaal in de bloedbuisjes. Wij nemen contact met je op om eventueel te herplannen voor deze waarden.',
  },
  {
    title: 'Folliumzuur (B9)',
    content:
      'Je hebt een klein foliumzuur tekort. Dit kan leiden tot vermoeidheid, een verminderde weerstand en in sommige gevallen concentratieproblemen. Het is verstandig om je voeding of supplement hierop aan te passen.',
  },
  {
    title: 'Vitamine B12',
    content:
      'Je Vitamine B12 waarden zitten in het lagere normale bereik, maar je zou deze waarde kunnen optimaliseren voor ondersteuning van je energieniveau.',
  },
  {
    title: 'Calcium',
    content:
      'Je Calcium waarde ligt boven de normale range. Kijk waar dit mogelijk (overmatig) in je voedingspatroon aanwezig is en probeer daarin iets te matigen (denk aan noten, bepaalde melk/yoghurt).',
  },
  {
    title: 'Natrium',
    content:
      'Een lichte verhoging van de Natrium waarde. Matig de hoeveelheid zout in je voeding en zorg dat je voldoende water drinkt iedere dag.',
  },
];

const initialLifestyleGoals = [
  'You want to improve your sleep',
  'Your energy can be better',
  'You indicated poor sleeping nights',
];

const initialState = {
  header: {
    type: 'Advanced',
    testDate: '19/02/2025',
    resultDate: '24/02/2025',
  },
  bloodTestResults: initialResults,
  doctorRecommendations: initialRecommendations,
  lifestyleGoals: initialLifestyleGoals,
  previousTests: [
    { orderId: 'N:33017935692', type: 'Advanced', testDate: '20.05.25' },
    { orderId: 'N:33017935693', type: 'Advanced', testDate: '18.03.25' },
    { orderId: 'N:33017935694', type: 'Advanced', testDate: '10.01.25' },
  ],
  reports: [],
};

const bloodTestSlice = createSlice({
  name: 'bloodTest',
  initialState,
  reducers: {
    setReports(state, action) {
      const next = action.payload;
      // Always replace list; never keep stale reports when payload is missing/invalid.
      state.reports = Array.isArray(next) ? next : [];
    },
    upsertReport(state, action) {
      const incoming = action.payload;
      if (!incoming || !incoming.header) return;
      const t = incoming.header?.type || '';
      const td = incoming.header?.testDate || '';
      const rd = incoming.header?.resultDate || '';
      const idx = state.reports.findIndex(
        (r) =>
          (r.header?.type || '') === t &&
          (r.header?.testDate || '') === td &&
          (r.header?.resultDate || '') === rd
      );
      if (idx === -1) {
        state.reports.push(incoming);
      } else {
        // Full replace so empty/missing fields from API do not inherit previous row.
        state.reports[idx] = incoming;
      }
    },
    setResults(state, action) {
      state.bloodTestResults = Array.isArray(action.payload)
        ? action.payload
        : state.bloodTestResults;
    },
    setRecommendations(state, action) {
      state.doctorRecommendations = Array.isArray(action.payload)
        ? action.payload
        : state.doctorRecommendations;
    },
    setLifestyleGoals(state, action) {
      state.lifestyleGoals = Array.isArray(action.payload)
        ? action.payload
        : state.lifestyleGoals;
    },
    setPreviousTests(state, action) {
      state.previousTests = Array.isArray(action.payload)
        ? action.payload
        : state.previousTests;
    },
  },
});

export const {
  setReports,
  setResults,
  setRecommendations,
  setLifestyleGoals,
  setPreviousTests,
} = bloodTestSlice.actions;

export default bloodTestSlice.reducer;
